import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BedDouble, Bus, CalendarDays, Check, FileWarning, Info, MapPin, MessageCircle, X } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import FaqAccordion from "@/components/FaqAccordion";
import { useEnquiry } from "@/components/EnquiryModal";
import { getPackage, useContent } from "@/lib/content";
import { tripTypeFor, waPackage } from "@/lib/site";
import BookNowButton from "@/components/BookNow";

export default function PackageDetail() {
  const { slug } = useParams();
  const { packages, loading } = useContent();
  const { openEnquiry } = useEnquiry();

  const pkg = getPackage(packages, slug);

  if (loading) return <div className="py-32 text-center text-slate-400">Loading package...</div>;
  if (!pkg) {
    return (
      <div className="py-32 text-center" data-testid="package-not-found">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Package not found</h1>
        <p className="mt-2 text-slate-500">It may have been updated — browse all holidays from the menu.</p>
      </div>
    );
  }

  const enquiryOpts = { packageName: pkg.title, destination: pkg.destination, tripType: tripTypeFor(pkg) };

  return (
    <div data-testid={`package-detail-${slug}`} className="pb-24 lg:pb-0">
      <SEO title={pkg.title} description={pkg.overview} />

      <section className="relative h-[55vh] min-h-[440px] flex items-end overflow-hidden grain">
        <img src={pkg.image} alt={`${pkg.title} — ${pkg.destination}`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navydeep/95 via-navy/45 to-navy/20" />
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <Link to={pkg.region === "India" ? "/india-holidays" : "/international-holidays"} data-testid="package-back-link"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-300 hover:text-white transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to {pkg.region === "India" ? "India" : "International"} Holidays
          </Link>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white max-w-3xl">{pkg.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-sm font-semibold text-white">
              <CalendarDays className="w-4 h-4" /> {pkg.nights} Nights / {pkg.days} Days
            </span>
            <Link to={`/destinations/${pkg.destination_slug}`} className="flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-sm font-semibold text-white hover:bg-white/25 transition-colors">
              <MapPin className="w-4 h-4" /> {pkg.destination}
            </Link>
            {(pkg.holiday_types || []).map((t) => (
              <span key={t} className="rounded-full bg-gold/90 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-navydeep">{t}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_360px] gap-10">
          <div>
            <Reveal>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Overview</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">{pkg.overview}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {(pkg.highlights || []).map((h) => (
                  <span key={h} className="flex items-center gap-1.5 rounded-full bg-sky-50 border border-sky-100 px-3.5 py-1.5 text-xs font-semibold text-royal">
                    <Check className="w-3.5 h-3.5" /> {h}
                  </span>
                ))}
              </div>
            </Reveal>

            <Reveal className="mt-12">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Day-Wise Itinerary</h2>
              <ol className="mt-6 space-y-0" data-testid="package-itinerary">
                {(pkg.itinerary || []).map((day, i) => (
                  <li key={day.day} className="relative pl-14 pb-8 last:pb-0">
                    {i < pkg.itinerary.length - 1 && <span className="absolute left-[22px] top-12 bottom-0 w-px bg-slate-200" />}
                    <span className="absolute left-0 top-0 w-11 h-11 rounded-xl bg-navy text-white font-heading font-bold text-sm flex items-center justify-center">
                      D{day.day}
                    </span>
                    <div className="rounded-2xl bg-white border border-slate-200/80 p-5 shadow-sm">
                      <h3 className="font-heading font-bold text-slate-900">Day {day.day} — {day.title}</h3>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{day.description}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Reveal>

            <Reveal className="mt-12 grid md:grid-cols-2 gap-6">
              <div className="rounded-2xl bg-white border border-slate-200/80 p-6 shadow-sm">
                <h3 className="font-heading font-bold text-slate-900">Inclusions</h3>
                <ul className="mt-4 space-y-2.5">
                  {(pkg.inclusions || []).map((x) => (
                    <li key={x} className="flex items-start gap-2 text-sm text-slate-600"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {x}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200/80 p-6 shadow-sm">
                <h3 className="font-heading font-bold text-slate-900">Exclusions</h3>
                <ul className="mt-4 space-y-2.5">
                  {(pkg.exclusions || []).map((x) => (
                    <li key={x} className="flex items-start gap-2 text-sm text-slate-600"><X className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" /> {x}</li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal className="mt-8 grid md:grid-cols-2 gap-6">
              <div className="rounded-2xl bg-white border border-slate-200/80 p-6 shadow-sm">
                <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><BedDouble className="w-5 h-5 text-skybrand" /> Hotel Information</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{pkg.hotels}</p>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200/80 p-6 shadow-sm">
                <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Bus className="w-5 h-5 text-skybrand" /> Transport</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{pkg.transport}</p>
              </div>
            </Reveal>

            <Reveal className="mt-8">
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-6">
                <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Info className="w-5 h-5 text-gold" /> Important Notes</h3>
                <ul className="mt-3 space-y-2">
                  {(pkg.notes || []).map((n) => <li key={n} className="text-sm text-slate-600 leading-relaxed">• {n}</li>)}
                </ul>
              </div>
              <div className="mt-4 rounded-2xl bg-white border border-slate-200/80 p-6 shadow-sm">
                <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><FileWarning className="w-5 h-5 text-skybrand" /> Cancellation Information</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{pkg.cancellation}</p>
              </div>
            </Reveal>

            <Reveal className="mt-12">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">FAQs</h2>
              <div className="mt-4"><FaqAccordion faqs={pkg.faqs || []} testidPrefix="package-faq" /></div>
            </Reveal>
          </div>

          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 rounded-2xl bg-navy p-7 text-white shadow-xl grain relative overflow-hidden">
              {pkg.price_from ? (
                <>
                  <p className="text-xs font-bold tracking-[0.25em] uppercase text-sky-300">Starting From</p>
                  <p className="mt-1 font-heading text-3xl font-extrabold text-white">
                    ₹{Number(pkg.price_from).toLocaleString("en-IN")}
                    <span className="ml-1.5 text-sm font-semibold text-slate-300">{pkg.price_basis || "per person"}</span>
                  </p>
                  <p className="mt-1.5 text-[11px] text-gold leading-snug">
                    {pkg.valid_till ? `Valid up to ${pkg.valid_till} · ` : ""}Tentative rate — final quote customised on request
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs font-bold tracking-[0.25em] uppercase text-sky-300">Customised Pricing</p>
                  <h3 className="mt-2 font-heading text-xl font-bold">Get a Custom Quote</h3>
                </>
              )}
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                Final costing depends on travel dates, hotel category, flights, number of travellers and customised requirements.
              </p>
              <div className="mt-5 space-y-2 text-sm">
                <p className="flex justify-between border-b border-white/10 pb-2"><span className="text-slate-400">Duration</span><span className="font-semibold">{pkg.nights}N / {pkg.days}D</span></p>
                <p className="flex justify-between border-b border-white/10 pb-2"><span className="text-slate-400">Destination</span><span className="font-semibold">{pkg.destination}</span></p>
                <p className="flex justify-between"><span className="text-slate-400">Type</span><span className="font-semibold">{(pkg.holiday_types || []).join(", ")}</span></p>
              </div>
              <button onClick={() => openEnquiry(enquiryOpts)} data-testid="package-detail-quote-button"
                      className="mt-6 w-full rounded-full bg-gold py-3.5 font-heading font-bold text-navydeep hover:bg-amber-400 transition-colors">
                GET A CUSTOM QUOTE
              </button>
              <BookNowButton pkg={pkg} testid="package-detail-book-now"
                             className="mt-3 w-full rounded-full border-2 border-gold py-3.5 font-heading font-bold text-gold hover:bg-gold hover:text-navydeep transition-colors">
                BOOK NOW — ₹5,000 ADVANCE
              </BookNowButton>
              <a href={waPackage(pkg.title)} target="_blank" rel="noopener noreferrer" data-testid="package-detail-whatsapp"
                 className="mt-3 w-full rounded-full bg-[#25D366] py-3.5 font-heading font-bold text-white hover:bg-[#1fb857] transition-colors flex items-center justify-center gap-2">
                <MessageCircle className="w-4 h-4" /> WhatsApp This Package
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* Mobile sticky bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 p-3 flex gap-2" data-testid="package-mobile-sticky-bar">
        <a href={waPackage(pkg.title)} target="_blank" rel="noopener noreferrer"
           className="flex-1 rounded-full bg-[#25D366] py-3 text-center text-xs font-bold text-white flex items-center justify-center">WhatsApp</a>
        <button onClick={() => openEnquiry(enquiryOpts)} data-testid="package-mobile-quote-button"
                className="flex-1 rounded-full bg-navy py-3 text-xs font-bold text-white">
          GET A QUOTE
        </button>
        <BookNowButton pkg={pkg} testid="package-mobile-book-now"
                       className="flex-1 rounded-full bg-gold py-3 text-xs font-bold text-navydeep">
          BOOK · ₹5,000
        </BookNowButton>
      </div>
    </div>
  );
}
